import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import { CONVERSATIONS_KEY } from '../messages/api';

import type {
  FollowUpDetailResponse,
  FollowUpIndexResponse,
  FollowUpItemResponse,
} from './types';

/**
 * The slice's whole HTTP surface (`code-organization.md` — `api.ts` is the frontend's
 * `repository.ts`).
 *
 * **Two terminal actions, and they are two different claims.** `useSendFollowUp` delivers the
 * draft onto the pair's thread and the item moves to `sent` because a message was sent
 * (`MEM-038` AC-1 and AC-3 under ADR 052); `useCopyFollowUp` records the member's own assertion
 * that they handled it, and sends nothing to anybody — the copying happens in the browser's
 * clipboard and that call is only the state change. Copy stayed as a secondary action on Sumit's
 * *"ok"* of 2026-09-11, and it is the route to somebody the member is no longer connected to,
 * where Send is refused.
 *
 * Every call needs a session. There is no unauthenticated view of anybody's follow-ups, and no
 * call here can name a member other than the caller — the API takes the member from the session.
 */

const INDEX_KEY = ['follow-up', 'mine'] as const;
const DETAIL_KEY = (eventId: string): readonly unknown[] => ['follow-up', 'event', eventId];

/** `MEM-036` AC-1 — the index, most recent first. */
export function useMyFollowUps(options: {
  readonly enabled: boolean;
}): UseQueryResult<FollowUpIndexResponse> {
  return useQuery({
    queryKey: INDEX_KEY,
    queryFn: () => apiRequest<FollowUpIndexResponse>('/api/follow-up/mine'),
    enabled: options.enabled,
    retry: false,
  });
}

/** `MEM-036` AC-2 … AC-6 — one event's follow-up, including the reduced case. */
export function useFollowUpForEvent(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<FollowUpDetailResponse> {
  return useQuery({
    queryKey: DETAIL_KEY(eventId),
    queryFn: () =>
      apiRequest<FollowUpDetailResponse>(
        `/api/follow-up/events/${encodeURIComponent(eventId)}`,
      ),
    enabled: options.enabled && eventId.length > 0,
    retry: false,
  });
}

interface ItemAction {
  readonly eventId: string;
  readonly attendeeRef: string;
}

/**
 * `MEM-038` AC-3 — the member copied it, so the item moves to `sent`.
 *
 * Named `copy`, not `send`, all the way down to the route. Nothing leaves the platform on this
 * call: the text is on the member's clipboard and they are about to paste it somewhere the
 * platform has no part in.
 */
/**
 * `MEM-038` AC-1 — **Send**, which delivers the draft as a message on the pair's thread
 * (`MEM-FUP-008`, `MEM-054` AC-4).
 *
 * The body carries the textarea's current contents, so Send does not depend on the draft having
 * been saved first: what somebody pressing Send means is the words in front of them.
 *
 * **The conversations list is invalidated too**, because a send from here creates or moves a
 * thread — the Messages page's rows and the navigation's unread state both changed, and a member
 * who sends four openers and then opens Messages should find four threads rather than a stale
 * empty state.
 *
 * A refusal — the pair disconnected since the summary was generated, or the day's new-thread
 * limit — comes back as an error and **leaves the item where it was**, which the card renders.
 */
export function useSendFollowUp(): UseMutationResult<FollowUpItemResponse, Error, ItemAction & { readonly body: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, attendeeRef, body }) =>
      apiRequest<FollowUpItemResponse>(
        `/api/follow-up/events/${encodeURIComponent(eventId)}/items/${encodeURIComponent(attendeeRef)}/send`,
        { method: 'POST', body: { body } },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: INDEX_KEY });
      await queryClient.invalidateQueries({ queryKey: ['follow-up', 'event'] });
      await queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
}

export function useCopyFollowUp(): UseMutationResult<FollowUpItemResponse, Error, ItemAction> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, attendeeRef }: ItemAction) =>
      apiRequest<FollowUpItemResponse>(
        `/api/follow-up/events/${encodeURIComponent(eventId)}/items/${encodeURIComponent(attendeeRef)}/copy`,
        { method: 'POST' },
      ),
    onSuccess: (_result, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: DETAIL_KEY(eventId) });
      void queryClient.invalidateQueries({ queryKey: INDEX_KEY });
    },
  });
}

/** `MEM-038` AC-4 — cleared without sending anything. */
export function useMarkFollowUpDone(): UseMutationResult<FollowUpItemResponse, Error, ItemAction> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, attendeeRef }: ItemAction) =>
      apiRequest<FollowUpItemResponse>(
        `/api/follow-up/events/${encodeURIComponent(eventId)}/items/${encodeURIComponent(attendeeRef)}/done`,
        { method: 'POST' },
      ),
    onSuccess: (_result, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: DETAIL_KEY(eventId) });
      void queryClient.invalidateQueries({ queryKey: INDEX_KEY });
    },
  });
}

/**
 * `MEM-037` AC-4 — the member's edited draft.
 *
 * Saved explicitly rather than on every keystroke, and **never merged with the openers**: the
 * request carries only `draft`, so there is no path by which a template can be re-applied over
 * something somebody wrote.
 */
export function useSaveFollowUpDraft(): UseMutationResult<
  FollowUpItemResponse,
  Error,
  ItemAction & { readonly draft: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, attendeeRef, draft }: ItemAction & { readonly draft: string }) =>
      apiRequest<FollowUpItemResponse>(
        `/api/follow-up/events/${encodeURIComponent(eventId)}/items/${encodeURIComponent(attendeeRef)}/draft`,
        { method: 'PUT', body: { draft } },
      ),
    onSuccess: (_result, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: DETAIL_KEY(eventId) });
    },
  });
}
